// In EditUserModal.jsx, update the component to fetch companies if not available
import React, { useEffect, useState } from 'react';
import { FaSearch } from 'react-icons/fa';

const EditUserModal = ({
  showEditModal,
  setShowEditModal,
  editingUser,
  setEditingUser,
  editUserData,
  setEditUserData,
  isSubmitting,
  companySearch,
  setCompanySearch,
  filteredCompanies,
  loadingCompanies,
  handleEditSubmit,
  handleCompanyCheckboxChange,
  companies // This should be passed from parent
}) => {
  // Add local state to handle companies if not passed
  const [localCompanies, setLocalCompanies] = useState([]);
  const [localFilteredCompanies, setLocalFilteredCompanies] = useState([]);
  const [localLoadingCompanies, setLocalLoadingCompanies] = useState(false);

  // Fetch companies if not available
  useEffect(() => {
    const fetchCompanies = async () => {
      // If companies are passed from parent, use them
      if (companies && companies.length > 0) {
        setLocalCompanies(companies);
        setLocalFilteredCompanies(companies);
        return;
      }
      
      // If filteredCompanies are available, use them
      if (filteredCompanies && filteredCompanies.length > 0) {
        setLocalCompanies(filteredCompanies);
        setLocalFilteredCompanies(filteredCompanies);
        return;
      }

      // Otherwise, fetch companies directly
      setLocalLoadingCompanies(true);
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const response = await fetch('/api/companies/', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setLocalCompanies(data);
          setLocalFilteredCompanies(data);
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
      } finally {
        setLocalLoadingCompanies(false);
      }
    };

    if (showEditModal) {
      fetchCompanies();
    }
  }, [showEditModal, companies, filteredCompanies]);

  // Filter companies based on search
  useEffect(() => {
    if (!companySearch.trim()) {
      setLocalFilteredCompanies(localCompanies);
    } else {
      const filtered = localCompanies.filter(company =>
        company.name.toLowerCase().includes(companySearch.toLowerCase()) ||
        (company.ticker_id && company.ticker_id.toLowerCase().includes(companySearch.toLowerCase()))
      );
      setLocalFilteredCompanies(filtered);
    }
  }, [companySearch, localCompanies]);

  // Use local companies if parent companies are not available
  const displayCompanies = companies && companies.length > 0 ? companies : localCompanies;
  const displayFilteredCompanies = filteredCompanies && filteredCompanies.length > 0 ? filteredCompanies : localFilteredCompanies;
  const displayLoadingCompanies = loadingCompanies || localLoadingCompanies;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          Edit User: {editingUser.email}
        </h3>
        <form onSubmit={handleEditSubmit}>
          <div className="space-y-4">
            {/* ... rest of the form remains the same ... */}
            
            {editUserData.role !== 'superadmin' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Assign Companies (Optional)
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                    - {editUserData.role === 'admin' ? 'This admin will manage selected companies' : 'This user will only access selected companies'}
                  </span>
                </label>
                
                <div className="relative mb-2">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type="text"
                    placeholder="Search companies..."
                    className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                  />
                </div>
                
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-48 overflow-y-auto">
                  {displayLoadingCompanies ? (
                    <div className="p-4 text-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mx-auto"></div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Loading companies...</p>
                    </div>
                  ) : displayFilteredCompanies.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                      {companySearch ? 'No companies found matching your search' : 'No companies available'}
                    </div>
                  ) : (
                    displayFilteredCompanies.map((company) => (
                      <div
                        key={company.id}
                        className={`flex items-center px-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                          editUserData.company_ids.includes(company.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          id={`edit-company-${company.id}`}
                          checked={editUserData.company_ids.includes(company.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleCompanyCheckboxChange(company.id, true);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <label 
                          htmlFor={`edit-company-${company.id}`} 
                          className="ml-3 flex-1 cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            handleCompanyCheckboxChange(company.id, true);
                          }}
                        >
                          <div className="text-sm text-gray-900 dark:text-white">{company.name}</div>
                          {company.ticker_id && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Ticker: {company.ticker_id}
                            </div>
                          )}
                        </label>
                      </div>
                    ))
                  )}
                </div>
                
                {editUserData.company_ids.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Selected: {editUserData.company_ids.length} company(s)
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {editUserData.company_ids.map(companyId => {
                        const company = displayCompanies.find(c => c.id === companyId);
                        return company ? (
                          <span 
                            key={companyId}
                            className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded"
                          >
                            {company.name}
                            <button
                              type="button"
                              className="ml-1 text-blue-600 hover:text-blue-800"
                              onClick={() => handleCompanyCheckboxChange(companyId, true)}
                            >
                              �
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* ... rest of the form remains the same ... */}
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => {
                setShowEditModal(false);
                setEditingUser(null);
                setCompanySearch('');
              }}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting || !editUserData.email.trim()}
            >
              {isSubmitting ? (
                <>
                  <span className="inline-block animate-spin mr-2">?</span>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;